/**
 * The edition (ADR 0035): a cover over a bare text plane, and the whole thing
 * one link. The plane holds the title (16/22, two lines then truncated), one
 * meta line in the time syntax (the caller formats it with `formatClassWhen`),
 * and at most one interruption. No border, no shadow; hover is the well around
 * cover and plane together; focus is a 2 px ring. Identity never empties: a
 * course with nothing to say shows its cover and title.
 */
import type { ReactNode } from "react";
import { Link } from "react-router";

import { EditionCover } from "@/components/workspace/EditionCover";
import { cn } from "@/lib/utils";

export function Edition({
  href,
  code,
  label,
  title,
  meta,
  interruption,
  variant = "grid",
  className,
}: {
  href: string;
  code: string;
  /** The department name when the payload carries one; never invented. */
  label?: string | null;
  title: string;
  meta?: ReactNode;
  interruption?: ReactNode;
  variant?: "grid" | "phone";
  className?: string;
}) {
  return (
    <Link
      to={href}
      data-slot="edition"
      className={cn(
        "flex min-w-0 flex-col gap-2.5 rounded-[1.25rem] p-2 text-foreground transition-colors duration-[var(--motion-fast)]",
        "hover:bg-well focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className,
      )}
    >
      <EditionCover code={code} label={label} variant={variant} />
      {/* Block children, so the link's accessible name keeps a space between
          the code, the title and the meta line. */}
      <div data-slot="edition-plane" className="flex min-w-0 flex-col gap-[3px] px-2 pt-0.5 pb-1.5">
        <div dir="auto" className="line-clamp-2 text-[1rem]/[1.375rem] font-semibold text-foreground [overflow-wrap:anywhere]">
          <div className="sr-only">{code}</div>
          {title}
        </div>
        {meta && <div className="text-meta tabular-nums text-muted-foreground">{meta}</div>}
        {interruption && (
          <div className="text-ui text-foreground">
            {interruption}
          </div>
        )}
      </div>
    </Link>
  );
}
